/**
 * Юридические документы сервиса — единственный источник данных о них (задача 18).
 *
 * Из этого файла берут всё трое: форма регистрации (подписи чекбоксов и адреса
 * ссылок), бэкенд (версии редакций, которые обязан принять пользователь) и сборка
 * страниц документов (`src/build/legalPages.ts`). Поэтому версия редакции меняется
 * в одном месте — здесь, — а текст документа — в `docs/legal/<slug>.md`.
 *
 * Версия — строка `<документ>-<дата редакции>` (`tos-2026-10-17`,
 * `consent-2026-10-17`): дата совпадает с датой редакции в тексте документа,
 * поэтому при правке текста её обновляют и в Markdown-файле, и здесь — иначе
 * сервер отклонит регистрацию с устаревшей версией (это защита от старого клиента).
 */

/** Слаг документа: он же имя файла `docs/legal/<slug>.md` и последний сегмент адреса. */
export type ConsentSlug = 'terms-of-service' | 'personal-data-consent' | 'personal-data-policy';

/** Юридический документ: название, адрес страницы и путь к тексту в репозитории. */
export interface ConsentDocument {
  slug: ConsentSlug;
  /** Название документа — заголовок его страницы и подпись ссылки. */
  title: string;
  /** Адрес страницы документа; страницы публикуются без аутентификации. */
  path: string;
  /** Путь к тексту документа: Markdown — источник правды, страница собирается из него. */
  sourcePath: string;
}

/**
 * Документ, который пользователь принимает: у него есть версия редакции, и она
 * фиксируется в профиле при регистрации. Политика обработки персональных данных
 * отдельным чекбоксом не принимается, поэтому версии у неё нет.
 */
export interface AcceptedConsentDocument extends ConsentDocument {
  /** Версия принятой редакции — её запрашивает и проверяет API регистрации. */
  version: string;
}

/** Адрес страницы документа: все документы публикуются в разделе /legal/. */
function legalPath(slug: ConsentSlug): string {
  return `/legal/${slug}`;
}

/** Пользовательское соглашение (принимается первым чекбоксом регистрации). */
export const TERMS_OF_SERVICE: AcceptedConsentDocument = {
  slug: 'terms-of-service',
  title: 'Пользовательское соглашение',
  path: legalPath('terms-of-service'),
  sourcePath: 'docs/legal/terms-of-service.md',
  version: 'tos-2026-10-17',
};

/**
 * Согласие на обработку персональных данных (принимается вторым чекбоксом).
 * Отдельный чекбокс — требование ч. 1 ст. 9 152-ФЗ (ред. 156-ФЗ): согласие на
 * обработку ПДн нельзя объединять с другими подтверждениями.
 */
export const PERSONAL_DATA_CONSENT: AcceptedConsentDocument = {
  slug: 'personal-data-consent',
  title: 'Соглашение об обработке и использовании персональных данных',
  path: legalPath('personal-data-consent'),
  sourcePath: 'docs/legal/personal-data-consent.md',
  version: 'consent-2026-10-17',
};

/** Политика обработки персональных данных: справочный документ, ссылка в подвале. */
export const PERSONAL_DATA_POLICY: ConsentDocument = {
  slug: 'personal-data-policy',
  title: 'Политика обработки персональных данных',
  path: legalPath('personal-data-policy'),
  sourcePath: 'docs/legal/personal-data-policy.md',
};

/** Все документы для публикации и ссылок в подвале — в порядке показа пользователю. */
export const CONSENT_DOCUMENTS: ConsentDocument[] = [
  TERMS_OF_SERVICE,
  PERSONAL_DATA_CONSENT,
  PERSONAL_DATA_POLICY,
];
